import { Link } from 'react-router';
import { ROUTES } from '@/app/router/routes';
import { buttonClasses } from '@/components/ui/button-styles';
import { EmptyState } from '@/components/feedback/EmptyState';
import { BriefcaseIcon } from '@/components/icons';
import { PageHeading, LoadingState, ErrorNotice } from '@/components/portal/PortalPage';
import { Badge } from '@/components/ui/Badge';

import { useMissions } from './usePortal';

/** Date au format français. Midi fixe : une date civile ne change pas de jour. */
const formatDay = (value: string | null): string =>
  value === null
    ? 'sans terme'
    : new Date(`${value}T12:00:00`).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

/** Liste des affectations ; chaque fiche donne accès au CJM et aux CRA de la mission. */
export const MissionsPage = () => {
  const query = useMissions();

  if (query.isPending) {
    return <LoadingState>Chargement de vos missions…</LoadingState>;
  }

  if (query.isError) {
    return (
      <ErrorNotice
        onRetry={() => {
          void query.refetch();
        }}
      >
        {query.error.message}
      </ErrorNotice>
    );
  }

  const missions = query.data;

  return (
    <div className="portal-page">
      <PageHeading
        title="Mes missions"
        description="Vos affectations, vos dates clés et vos droits à congés."
        icon={<BriefcaseIcon />}
      />

      {missions.length === 0 ? (
        <EmptyState
          className="my-8"
          icon={<BriefcaseIcon />}
          title="Aucune mission"
          description="Votre interlocuteur ubeefy vous préviendra au démarrage de votre première mission."
        />
      ) : (
        <ul className="portal-mission-grid">
          {missions.map((mission) => (
            <li key={mission.id} className="portal-surface portal-mission">
              <div className="portal-mission__heading">
                <span className="portal-row-icon" aria-hidden>
                  <BriefcaseIcon />
                </span>
                <div className="min-w-0">
                  <h2 className="text-navy-900 text-sm font-semibold wrap-anywhere">
                    {mission.clientName ?? mission.projectName}
                  </h2>
                  <p className="text-ink-500 mt-0.5 text-xs">
                    {mission.title ?? mission.projectName}
                    {mission.reference ? ` · ${mission.reference}` : ''}
                  </p>
                </div>
                <Badge tone={mission.isActive ? 'success' : 'muted'}>
                  {mission.isActive ? 'En cours' : mission.status.label}
                </Badge>
              </div>

              <dl className="portal-facts">
                <div className="min-w-0">
                  <dt className="text-ink-500 text-xs">Début</dt>
                  <dd className="text-navy-900 mt-1 text-sm">{formatDay(mission.startDate)}</dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-ink-500 text-xs">Fin</dt>
                  <dd className="text-navy-900 mt-1 text-sm">{formatDay(mission.endDate)}</dd>
                </div>
                <div className="col-span-2 min-w-0">
                  <dt className="text-ink-500 text-xs">Droits à congés</dt>
                  <dd className="text-navy-900 mt-1 text-sm">
                    {mission.leaveEntitlement.total === 0 ? (
                      <span className="text-ink-400">Aucun droit ouvert sur cette mission</span>
                    ) : (
                      <>
                        {mission.leaveEntitlement.paidLeave} jours de congés payés
                        {mission.leaveEntitlement.rtt > 0
                          ? `, ${String(mission.leaveEntitlement.rtt)} RTT`
                          : ''}
                        {mission.leaveEntitlement.seniority > 0
                          ? `, ${String(mission.leaveEntitlement.seniority)} d’ancienneté`
                          : ''}
                      </>
                    )}
                  </dd>
                </div>
              </dl>
              <Link
                to={ROUTES.mission(mission.id)}
                className={buttonClasses({ size: 'sm', variant: 'secondary' })}
              >
                Voir la mission et ses CRA
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
